import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Registro, NOMBRE_MAX, TELEFONO_MAX } from './registro.model';
import { RegistrosService } from './registros.service';

/** Rechaza valores formados solo por espacios (la API exige contenido). */
export function sinSoloEspacios(control: AbstractControl): ValidationErrors | null {
  const valor = control.value;
  return typeof valor === 'string' && valor.length > 0 && valor.trim().length === 0 ? { vacio: true } : null;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  private readonly api = inject(RegistrosService);
  private readonly fb = inject(FormBuilder);

  readonly nombreMax = NOMBRE_MAX;
  readonly telefonoMax = TELEFONO_MAX;

  readonly registros = signal<Registro[]>([]);
  readonly cargando = signal(true);
  readonly errorCarga = signal<string | null>(null);
  readonly editando = signal<Registro | null>(null);
  readonly guardando = signal(false);
  readonly errorGuardado = signal<string | null>(null);
  readonly erroresServidor = signal<Record<string, string>>({});
  readonly confirmandoId = signal<number | null>(null);
  readonly borrandoId = signal<number | null>(null);
  readonly errorBorrado = signal<string | null>(null);
  readonly aviso = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    nombre: ['', [Validators.required, Validators.maxLength(NOMBRE_MAX), sinSoloEspacios]],
    telefono: ['', [Validators.required, Validators.maxLength(TELEFONO_MAX), sinSoloEspacios]],
  });

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando.set(true);
    this.errorCarga.set(null);
    this.api.listar().subscribe({
      next: (lista) => {
        this.registros.set(lista);
        this.cargando.set(false);
      },
      error: (e) => {
        this.errorCarga.set(this.mensaje(e, 'No se pudo cargar el listado.'));
        this.cargando.set(false);
      },
    });
  }

  editar(registro: Registro): void {
    this.editando.set(registro);
    this.confirmandoId.set(null);
    this.errorGuardado.set(null);
    this.erroresServidor.set({});
    this.aviso.set(null);
    this.form.reset({ nombre: registro.nombre, telefono: registro.telefono });
    document.getElementById('nombre')?.focus();
  }

  cancelarEdicion(): void {
    this.editando.set(null);
    this.errorGuardado.set(null);
    this.erroresServidor.set({});
    this.form.reset({ nombre: '', telefono: '' });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const valores = {
      nombre: this.form.controls.nombre.value.trim(),
      telefono: this.form.controls.telefono.value.trim(),
    };
    const actual = this.editando();
    this.guardando.set(true);
    this.errorGuardado.set(null);
    this.erroresServidor.set({});
    this.aviso.set(null);

    const peticion = actual ? this.api.actualizar(actual.id, valores) : this.api.crear(valores);
    peticion.subscribe({
      next: (guardado) => {
        this.guardando.set(false);
        this.aviso.set(actual ? 'Registro actualizado.' : 'Registro creado.');
        this.editando.set(null);
        this.form.reset({ nombre: '', telefono: '' });
        if (actual) {
          this.registros.update((l) => l.map((r) => (r.id === guardado.id ? guardado : r)));
        } else {
          this.registros.update((l) => [...l, guardado]);
        }
      },
      error: (e: HttpErrorResponse) => {
        this.guardando.set(false);
        this.erroresServidor.set(this.erroresDeCampo(e));
        this.errorGuardado.set(this.mensaje(e, 'No se pudo guardar el registro.'));
      },
    });
  }

  pedirBorrado(registro: Registro): void {
    this.errorBorrado.set(null);
    this.aviso.set(null);
    this.confirmandoId.set(registro.id);
  }

  cancelarBorrado(): void {
    this.confirmandoId.set(null);
  }

  confirmarBorrado(registro: Registro): void {
    this.borrandoId.set(registro.id);
    this.errorBorrado.set(null);
    this.api.eliminar(registro.id).subscribe({
      next: () => {
        this.borrandoId.set(null);
        this.confirmandoId.set(null);
        this.registros.update((l) => l.filter((r) => r.id !== registro.id));
        if (this.editando()?.id === registro.id) {
          this.cancelarEdicion();
        }
        this.aviso.set('Registro eliminado.');
      },
      error: (e) => {
        this.borrandoId.set(null);
        this.errorBorrado.set(this.mensaje(e, 'No se pudo eliminar el registro.'));
      },
    });
  }

  trackId(_: number, r: Registro): number {
    return r.id;
  }

  private erroresDeCampo(e: HttpErrorResponse): Record<string, string> {
    const resultado: Record<string, string> = {};
    const errores = e.error?.errors;
    if (e.status === 422 && errores && typeof errores === 'object') {
      for (const campo of Object.keys(errores)) {
        resultado[campo] = Object.values(errores[campo] as Record<string, string>).join(' ');
      }
    }
    return resultado;
  }

  private mensaje(e: unknown, porDefecto: string): string {
    if (e instanceof HttpErrorResponse) {
      if (e.status === 0) {
        return `${porDefecto} No hay conexión con el servidor.`;
      }
      if (e.status === 422) {
        return 'Revisa los datos: el servidor ha rechazado el formulario.';
      }
      if (e.status === 404) {
        return `${porDefecto} El registro ya no existe.`;
      }
      return `${porDefecto} (error ${e.status})`;
    }
    return porDefecto;
  }
}
